// src/components/DailyRewardModal.tsx
import React, { useMemo } from 'react';
import { getRoddoTheme, resolveRoddoDarkMode } from '../constants/theme';
import { XIcon, SparklesIcon, CheckCircleIcon } from './icons';
import type { DailyRewardPreview } from '../services/dailyRewards';

type Props = {
  isOpen: boolean;
  preview: DailyRewardPreview | null;
  isDarkMode?: boolean;
  busy?: boolean;
  onClaim: () => void | Promise<void>;
  onDismiss: () => void;
};

const DailyRewardModal: React.FC<Props> = ({
  isOpen,
  preview,
  isDarkMode,
  busy = false,
  onClaim,
  onDismiss,
}) => {
  const dark = resolveRoddoDarkMode(isDarkMode);
  const theme = getRoddoTheme(dark);

  const dots = useMemo(() => Array.from({ length: 20 }, (_, index) => index + 1), []);

  if (!isOpen || !preview) return null;

  const cycleDay = Math.max(1, Math.min(20, preview.cycleDay || 1));
  const hasCosmetic = !!preview.cosmeticName;
  const task = preview.dailyTask || null;

  return (
    <div
      className="fixed inset-0 z-[1180] flex items-center justify-center p-4 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-labelledby="daily-reward-title"
      onClick={busy ? undefined : onDismiss}
      style={{
        background: dark ? 'rgba(9,13,24,0.72)' : 'rgba(32, 24, 14, 0.24)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-[20px] border p-5 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: dark ? '#101827' : '#FFFCF7',
          borderColor: theme.borderStrong,
          color: theme.text,
          boxShadow: dark
            ? '0 8px 22px rgba(0,0,0,0.24)'
            : '0 12px 30px rgba(89,65,37,0.10)',
        }}
      >

        <div className="relative z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div
                className="grid h-12 w-12 place-items-center rounded-[14px] border"
                style={{
                  background: dark ? '#0F1828' : '#FFF9F1',
                  borderColor: theme.borderStrong,
                  boxShadow: dark
                    ? '0 4px 12px rgba(0,0,0,0.14)'
                    : '0 12px 26px rgba(89,65,37,0.12)',
                  color: '#F2B84B',
                }}
              >
                <SparklesIcon className="h-6 w-6" />
              </div>

              <div>
                <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: theme.textMuted }}>
                  Roddo Daily
                </p>
                <h2 id="daily-reward-title" className="mt-1 text-2xl font-black tracking-tight" style={{ color: theme.text }}>
                  Today&apos;s Cast
                </h2>
                <p className="mt-2 text-sm leading-6" style={{ color: theme.textSoft }}>
                  Open Roddo, check the water, and keep your fishing streak alive.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[12px] border transition-transform active:scale-95 disabled:opacity-50"
              aria-label="Close Today&apos;s Cast"
              style={{
                borderColor: theme.border,
                background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.66)',
                color: theme.text,
              }}
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>

          <div
            className="mt-5 rounded-[16px] border p-4"
            style={{
              background: dark ? '#0F1828' : '#FFF9F1',
              borderColor: theme.border,
            }}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.18em]" style={{ color: theme.textMuted }}>
                  Streak
                </p>
                <p className="mt-1 text-3xl font-black" style={{ color: theme.text }}>
                  Day {cycleDay}<span className="text-base font-extrabold" style={{ color: theme.textMuted }}> / 20</span>
                </p>
              </div>

              <div className="text-right">
                <p className="text-xs font-extrabold uppercase tracking-[0.18em]" style={{ color: theme.textMuted }}>
                  Reward
                </p>
                <p className="mt-1 text-2xl font-black" style={{ color: '#F2B84B' }}>
                  +{preview.shells} Shells
                </p>
                <p className="mt-1 text-xs font-black" style={{ color: dark ? '#A9EEF5' : '#2F80ED' }}>
                  +{preview.searchLures} Search Lures 🪝
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-10 gap-1.5" aria-label={`Day ${cycleDay} of 20`}>
              {dots.map((day) => {
                const active = day <= cycleDay;
                const milestone = day === 5 || day === 10 || day === 15 || day === 20;
                return (
                  <span
                    key={day}
                    className="h-2 rounded-full"
                    style={{
                      background: active
                        ? milestone
                          ? '#F2B84B'
                          : '#8FD0CA'
                        : dark
                          ? 'rgba(255,255,255,0.12)'
                          : 'rgba(49,72,110,0.14)',
                      boxShadow: active ? '0 0 12px rgba(143,208,202,0.26)' : undefined,
                    }}
                  />
                );
              })}
            </div>

            <div
              className="mt-4 rounded-[12px] border px-3 py-2.5"
              style={{
                background: dark ? 'rgba(103,216,232,0.08)' : 'rgba(47,128,237,0.07)',
                borderColor: dark ? 'rgba(103,216,232,0.18)' : 'rgba(47,128,237,0.14)',
              }}
            >
              <p className="text-[11px] font-black" style={{ color: dark ? '#A9EEF5' : '#245F91' }}>
                Search Lures refill naturally +1 every 3 hours, up to 30. Today&apos;s Cast adds +5.
              </p>
            </div>

            {hasCosmetic ? (
              <div
                className="mt-4 flex items-center gap-3 rounded-[12px] border p-3"
                style={{
                  background: dark ? 'rgba(253,143,90,0.10)' : 'rgba(253,143,90,0.12)',
                  borderColor: dark ? 'rgba(253,143,90,0.24)' : 'rgba(253,143,90,0.20)',
                }}
              >
                <div className="grid h-10 w-10 place-items-center rounded-[12px] bg-white/70 text-xl">
                  {preview.cosmeticEmoji || '🎣'}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-black" style={{ color: theme.text }}>
                    Milestone drop available
                  </p>
                  <p className="truncate text-xs font-bold" style={{ color: theme.textSoft }}>
                    {preview.cosmeticName}
                  </p>
                </div>
              </div>
            ) : null}

            {task ? (
              <div
                className="mt-4 rounded-[12px] border p-3"
                style={{
                  background: task.completed
                    ? dark ? 'rgba(143,208,202,0.12)' : 'rgba(143,208,202,0.18)'
                    : dark ? '#0C1423' : 'rgba(255,255,255,0.62)',
                  borderColor: task.completed
                    ? dark ? 'rgba(143,208,202,0.30)' : 'rgba(143,208,202,0.36)'
                    : theme.border,
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-black uppercase tracking-[0.18em]" style={{ color: theme.textMuted }}>
                      Daily task
                    </p>
                    <p className="mt-1 text-sm font-black" style={{ color: theme.text }}>
                      {task.completed ? '✅ ' : ''}{task.title}
                    </p>
                    <p className="mt-1 text-xs font-bold leading-5" style={{ color: theme.textSoft }}>
                      {task.description}
                    </p>
                  </div>

                  <div
                    className="flex-shrink-0 rounded-[12px] px-3 py-2 text-right"
                    style={{
                      background: dark ? 'rgba(253,143,90,0.12)' : 'rgba(253,143,90,0.13)',
                      color: '#F2B84B',
                    }}
                  >
                    <p className="text-[10px] font-black uppercase tracking-[0.16em]">Bonus</p>
                    <p className="text-sm font-black">+{task.rewardShells}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
            <button
              type="button"
              onClick={onClaim}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-[12px] border px-5 py-3 text-sm font-black transition-transform active:translate-y-[1px] disabled:opacity-65"
              style={{
                borderColor: 'rgba(255,255,255,0.22)',
                background: busy ? '#D7B75C' : '#F2B84B',
                color: '#2F2410',
                boxShadow: '0 5px 14px rgba(181,126,24,0.18)',
              }}
            >
              <CheckCircleIcon className="h-5 w-5" />
              {busy ? 'Claiming…' : 'Start Today\'s Cast'}
            </button>

            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="rounded-[12px] border px-5 py-3 text-sm font-extrabold transition-transform active:translate-y-[1px] disabled:opacity-65"
              style={{
                borderColor: theme.borderStrong,
                background: dark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.72)',
                color: theme.text,
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyRewardModal;
